import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { selectIsShadowModeEnabled } from '../config/config.selector';
import { AgentMode } from './agentMode.constants';

const selectAgentModeState = (state: RootState) => state.agentMode;

const selectAgentMode = createSelector(
  selectAgentModeState,
  (agentMode) => agentMode.mode
);

/**
 * True once the agent has actually acted in `Intercept` — an
 * `agent_interception` has gone out this session. Until then an `Intercept`
 * selection is reversible, because nothing has left the terminal.
 */
const selectIsAgentModeEngaged = createSelector(
  selectAgentModeState,
  (agentMode) => agentMode.engaged
);

/** `Intercept` selected but nothing sent yet — permission without consequence. */
const selectIsInterceptArmed = createSelector(
  selectAgentMode,
  selectIsAgentModeEngaged,
  (mode, engaged) => mode === AgentMode.intercept && !engaged
);

/**
 * The agent owns the order for real: `Intercept` **and** they have acted.
 *
 * NOT read by A4 (HITL-1606), despite what this comment promised when HITL-1604
 * added it. A4 migrates no outbound gate at all — it refuses agent actions on
 * the mode alone (`selectIsAgentActionBlocked` below) and leaves every
 * `isAIAutoMode` reader untouched. Kept because it is still the precise
 * "the take-over is real" predicate, which Phase C needs.
 */
const selectIsInterceptEngaged = createSelector(
  selectAgentMode,
  selectIsAgentModeEngaged,
  (mode, engaged) => mode === AgentMode.intercept && engaged
);

/** Both silent modes: nothing the agent does here reaches the customer. */
const selectIsAgentModeSilent = createSelector(
  selectAgentMode,
  (mode) => mode !== AgentMode.intercept
);

/**
 * HITL-1606 — the one condition in front of every agent action.
 *
 * True when Supervised AI mode is on AND the agent has not chosen `Intercept`:
 * they may look, but nothing they do may leave the terminal. Read in two places
 * — the choke point in `useAgentInterception`, which is the whole of the safety
 * story, and the action controls, which disable themselves so the agent is never
 * invited to attempt something that would be refused.
 *
 * Keyed on the MODE, deliberately not on `engaged`. `engaged` only latches once
 * an interception has gone out, so reading it here would be circular: the gate
 * would be opened by the very first action it exists to refuse.
 *
 * ANDed with the flag, so with `hitl_shadow_mode` off this is always false and
 * every surface behaves exactly as it does today.
 */
const selectIsAgentActionBlocked = createSelector(
  selectIsShadowModeEnabled,
  selectIsAgentModeSilent,
  (isShadowModeEnabled, isSilent) => isShadowModeEnabled && isSilent
);

/**
 * HITL-1610 — does the AI still own the centre pane?
 *
 * True when Supervised AI mode is off, which is today's behaviour on every path.
 * With the feature on the pane is the agent's in both modes: an arriving
 * hypothesis frame must move neither the open item nor the open modifier group,
 * both of which `selectItemAndResetNavigation` clears.
 *
 * Read only by the AI's replay — `addItemToCart` when `addedBy` is AI, and
 * `addHypothesisItemToCart`. Every human selection path is left alone.
 *
 * HITL-1647 collapsed this to the flag alone. It previously also returned true
 * in `Supervise`, where the agent was only reading. That branch is not lost
 * behaviour — it is unreachable, because there is no longer a mode in which the
 * agent has no cart of their own to be interrupted in. `Shadow` and `Intercept`
 * both answered `false` before this change and both answer `false` now.
 */
const selectShouldAIDriveItemSelection = createSelector(
  selectIsShadowModeEnabled,
  (isShadowModeEnabled) => !isShadowModeEnabled
);

/**
 * HITL-1614 — is the agent's own cart on screen?
 *
 * HITL-1647: whenever the feature is on. With `Supervise` retired there is no
 * mode in which the agent has nothing to build into, so the column is simply
 * part of what the feature IS — and an agent assigned a task can start helping
 * without first choosing a mode. With the flag off there is no second column at
 * all, exactly as in production today.
 *
 * This drives PANE SIZES, never the mount of the column that holds
 * `OrderReview`: that component has an unguarded mount effect dispatching
 * `sendOrder` (`OrderReview.tsx:174`), so remounting it in `Intercept` would put
 * an order on the POS as a side effect of a layout decision. See `RightSidebar`.
 */
const selectShouldShowMyCart = selectIsShadowModeEnabled;

/**
 * HITL-1614 — may the agent copy an AI line into My Cart?
 *
 * `Shadow` only. In `Intercept` the AI's lines already ARE the order, so there
 * is nothing to copy into a draft and the control is not rendered.
 */
const selectCanCopyToDraft = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    isShadowModeEnabled && mode === AgentMode.shadow
);

/**
 * HITL-1614 — the right column's own name.
 *
 * True whenever that cart is the order: flag off, or `Intercept`, where the
 * agent has taken over and the AI is silent. False in `Shadow`, where it is the
 * AI's work and the agent is watching it rather than owning it.
 *
 * HITL-1647 took its last consumer away — the column is now titled `Live Order`
 * in both modes, so the title no longer branches on this. Kept because the
 * predicate itself is still exactly true and still the precise "whose cart is
 * this?" question; it simply has no reader today.
 */
const selectIsAICartTheOrder = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    !isShadowModeEnabled || mode === AgentMode.intercept
);

/**
 * HITL-1615 — may the agent move My Cart into the order?
 *
 * `Intercept` only, and it is the exact complement of `selectCanCopyToDraft`:
 * copying is what you do while the AI drives, promoting is what you do once you
 * have taken over. The two controls are never on screen together.
 */
const selectCanPromoteToOrder = createSelector(
  selectIsShadowModeEnabled,
  selectAgentMode,
  (isShadowModeEnabled, mode) =>
    isShadowModeEnabled && mode === AgentMode.intercept
);

export {
  selectAgentModeState,
  selectAgentMode,
  selectIsAgentModeEngaged,
  selectIsInterceptArmed,
  selectIsInterceptEngaged,
  selectIsAgentModeSilent,
  selectIsAgentActionBlocked,
  selectShouldAIDriveItemSelection,
  selectShouldShowMyCart,
  selectCanCopyToDraft,
  selectCanPromoteToOrder,
  selectIsAICartTheOrder,
};
